import React, { useState, useEffect } from 'react';
import { ChevronRight, Award, Trophy, Medal, CheckCircle, Users, Clock, Target, Sparkles, Zap, Star, TrendingUp, User, Mail, FileText, Lightbulb, Phone, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const InnovationChallenge = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    identity: '',
    domain: '',
    problemDescription: '',
    problemImportance: '',
    hasPrototype: '',
    prototypeDetails: '',
    solutionIdea: '',
    solutionType: '',
    confidenceRating: 0,
    agreeRules: '',
    name: '',
    email: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const totalSteps = 6;
  const progress = (currentStep / totalSteps) * 100;

  const identityOptions = [
    { id: 'undergraduate', label: 'Undergraduate Student', icon: '🎓' },
    { id: 'md-student', label: 'MD Student', icon: '📚' },
    { id: 'resident', label: 'Resident', icon: '👨‍⚕️' },
    { id: 'attending', label: 'Attending Physician', icon: '⚕️' }
  ];

  const domainOptions = [
    'Obstetrics & Gynecology (OBG)',
    'Emergency & Trauma',
    'Radiology',
    'Laboratory Diagnostics',
    'Operation Theatre Management',
    'Anesthesia Monitoring',
    'Dialysis & Nephrology',
    'Public Health',
    'Telemedicine / Digital Health',
    'Hospital Management',
    'Artificial Intelligence in Medicine'
  ];

  const solutionTypes = [
    { id: 'mobile', label: 'Mobile App', icon: '📱' },
    { id: 'web', label: 'Web App', icon: '🌐' },
    { id: 'hardware', label: 'Hardware Prototype', icon: '🔧' },
    { id: 'ai-ml', label: 'AI/ML-based Model', icon: '🤖' },
    { id: 'workflow', label: 'Workflow/Process Innovation', icon: '⚙️' },
    { id: 'combined', label: 'Combined (Software + Hardware)', icon: '🔗' }
  ];

  // Helper function to get identity label from id
  const getIdentityLabel = (id) => {
    const option = identityOptions.find(opt => opt.id === id);
    return option ? option.label : id;
  };

  // Helper function to get solution type label from id
  const getSolutionTypeLabel = (id) => {
    const type = solutionTypes.find(t => t.id === id);
    return type ? type.label : id;
  };

  const prizes = [
    {
      place: '2nd Place',
      title: 'Innovator Award',
      prize: 'Ray-Ban Meta Smart Glasses',
      image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=400&q=80',
      color: '#C0C0C0',
      position: 'left'
    },
    {
      place: '1st Place',
      title: 'Visionary Award',
      prize: 'Oculus Quest 3',
      image: 'https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?w=400&q=80',
      color: '#F4CE7B',
      position: 'center'
    },
    {
      place: '3rd Place',
      title: 'Impact Award',
      prize: 'Apple Watch Ultra',
      image: 'https://images.unsplash.com/photo-1579586337278-3befd40fd17a?w=400&q=80',
      color: '#CD7F32',
      position: 'right'
    }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    // Map form data to API payload
    const payload = {
      identity: getIdentityLabel(formData.identity),
      problemDomain: formData.domain,
      problemDescription: formData.problemDescription,
      problemImportance: formData.problemImportance,
      hasPreviousProject: formData.hasPrototype === 'yes',
      solutionDescription: formData.solutionIdea,
      solutionType: getSolutionTypeLabel(formData.solutionType),
      confidenceRating: formData.confidenceRating,
      agreedToRules: formData.agreeRules === 'yes',
      fullName: formData.name,
      email: formData.email,
      phoneNumber: formData.phone
    };

    try {
      const response = await fetch('https://api.gewo.health/api/innovation-challenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to submit. Please try again.');
      }

      setSubmitted(true);
      console.log('Form submitted successfully:', payload);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.identity !== '';
      case 2:
        return formData.domain !== '' &&
          formData.problemDescription.trim() !== '' &&
          formData.problemImportance.trim() !== '';
      case 3:
        return formData.hasPrototype !== '' &&
          (formData.hasPrototype === 'no' || formData.prototypeDetails.trim() !== '');
      case 4:
        return formData.solutionIdea.trim() !== '' &&
          formData.solutionType !== '' &&
          formData.confidenceRating > 0;
      case 5:
        return formData.agreeRules === 'yes';
      case 6:
        return formData.name.trim() !== '' &&
          formData.email.trim() !== '' &&
          formData.phone.trim() !== '';
      default:
        return false;
    }
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('submission-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />

      {/* Enhanced Animated Background */}
      <div style={styles.networkBackground}>
        <svg style={styles.networkSvg} viewBox="0 0 1000 800">
          <defs>
            <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" style={{ stopColor: '#F4CE7B', stopOpacity: 0.8 }} />
              <stop offset="100%" style={{ stopColor: '#F4CE7B', stopOpacity: 0 }} />
            </radialGradient>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#F4CE7B', stopOpacity: 0.1 }} />
              <stop offset="50%" style={{ stopColor: '#F4CE7B', stopOpacity: 0.4 }} />
              <stop offset="100%" style={{ stopColor: '#F4CE7B', stopOpacity: 0.1 }} />
            </linearGradient>
          </defs>

          {/* Animated Grid Pattern */}
          {Array.from({ length: 20 }).map((_, i) => (
            <line
              key={`v-${i}`}
              x1={i * 50}
              y1="0"
              x2={i * 50}
              y2="800"
              stroke="rgba(244, 206, 123, 0.05)"
              strokeWidth="1"
            />
          ))}
          {Array.from({ length: 16 }).map((_, i) => (
            <line
              key={`h-${i}`}
              x1="0"
              y1={i * 50}
              x2="1000"
              y2={i * 50}
              stroke="rgba(244, 206, 123, 0.05)"
              strokeWidth="1"
            />
          ))}

          {/* Network Nodes and Lines */}
          <line x1="100" y1="100" x2="300" y2="200" stroke="url(#lineGradient)" strokeWidth="2" className="network-line" />
          <line x1="300" y1="200" x2="500" y2="150" stroke="url(#lineGradient)" strokeWidth="2" className="network-line" />
          <line x1="500" y1="150" x2="700" y2="250" stroke="url(#lineGradient)" strokeWidth="2" className="network-line" />
          <line x1="700" y1="250" x2="900" y2="200" stroke="url(#lineGradient)" strokeWidth="2" className="network-line" />
          <line x1="200" y1="400" x2="400" y2="500" stroke="url(#lineGradient)" strokeWidth="2" className="network-line" />
          <line x1="400" y1="500" x2="600" y2="450" stroke="url(#lineGradient)" strokeWidth="2" className="network-line" />
          <line x1="600" y1="450" x2="800" y2="550" stroke="url(#lineGradient)" strokeWidth="2" className="network-line" />
          <line x1="150" y1="300" x2="350" y2="350" stroke="url(#lineGradient)" strokeWidth="2" className="network-line" />
          <line x1="550" y1="350" x2="750" y2="400" stroke="url(#lineGradient)" strokeWidth="2" className="network-line" />

          <circle cx="100" cy="100" r="10" fill="url(#nodeGlow)" className="network-node node-1" />
          <circle cx="300" cy="200" r="10" fill="url(#nodeGlow)" className="network-node node-2" />
          <circle cx="500" cy="150" r="10" fill="url(#nodeGlow)" className="network-node node-3" />
          <circle cx="700" cy="250" r="10" fill="url(#nodeGlow)" className="network-node node-4" />
          <circle cx="900" cy="200" r="10" fill="url(#nodeGlow)" className="network-node node-5" />
          <circle cx="200" cy="400" r="10" fill="url(#nodeGlow)" className="network-node node-6" />
          <circle cx="400" cy="500" r="10" fill="url(#nodeGlow)" className="network-node node-7" />
          <circle cx="600" cy="450" r="10" fill="url(#nodeGlow)" className="network-node node-8" />
          <circle cx="800" cy="550" r="10" fill="url(#nodeGlow)" className="network-node node-9" />
          <circle cx="150" cy="300" r="10" fill="url(#nodeGlow)" className="network-node node-10" />
          <circle cx="350" cy="350" r="10" fill="url(#nodeGlow)" className="network-node node-11" />
          <circle cx="550" cy="350" r="10" fill="url(#nodeGlow)" className="network-node node-12" />
          <circle cx="750" cy="400" r="10" fill="url(#nodeGlow)" className="network-node node-13" />
        </svg>

        {/* Particle Effect Overlay */}
        <div style={styles.particleOverlay}>
          {Array.from({ length: 30 }).map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 10}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Enhanced Hero Section */}
      <section style={styles.heroSection}>
        <div style={styles.heroContainer}>
          <div style={styles.heroContent}>
            {/* Innovation Badge */}
            <div style={styles.innovationBadge} className="fade-in-up">
              <Trophy size={20} color="#F4CE7B" />
              <span>Innovation Challenge 2025</span>
            </div>

            {/* Main Headline */}
            <h1 style={styles.heroTitle} className="fade-in-up-delay">
              <span style={styles.titleHighlight}>Reimagine</span> the Future
              <br />
              of <span style={styles.titleGradient}>Medicine</span>
            </h1>

            {/* Enhanced Subtitle */}
            <p style={styles.heroSubtitle} className="fade-in-up-delay-2">
              From Admin to Bedside – Submit your idea to fix the system.
              <br />
              <span style={styles.subtitleEmphasis}>Your innovation could transform healthcare for millions.</span>
            </p>

            {/* Key Points */}
            <div style={styles.keyPoints} className="fade-in-up-delay-3">
              <div style={styles.keyPoint}>
                <div style={styles.keyPointIcon}>
                  <CheckCircle size={20} color="#F4CE7B" />
                </div>
                <span style={styles.keyPointText}>Open to all medical professionals</span>
              </div>
              <div style={styles.keyPoint}>
                <div style={styles.keyPointIcon}>
                  <CheckCircle size={20} color="#F4CE7B" />
                </div>
                <span style={styles.keyPointText}>Win premium tech prizes</span>
              </div>
              <div style={styles.keyPoint}>
                <div style={styles.keyPointIcon}>
                  <CheckCircle size={20} color="#F4CE7B" />
                </div>
                <span style={styles.keyPointText}>Shape the future of healthcare</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div style={styles.heroButtons} className="fade-in-up-delay-4">
              <button style={styles.heroCTA} onClick={scrollToForm} className="pulse-button">
                <Trophy size={22} style={{ marginRight: '10px' }} />
                Submit Your Vision
                <ChevronRight size={24} style={{ marginLeft: '10px' }} />
              </button>
              <button style={styles.heroSecondaryBtn} onClick={() => document.getElementById('rewards-section').scrollIntoView({ behavior: 'smooth' })}>
                View Prizes
                <Award size={20} style={{ marginLeft: '8px' }} />
              </button>
            </div>

            {/* Stats Bar */}
            <div style={styles.statsBar} className="fade-in-up-delay-5">
              <div style={styles.statItem}>
                <div style={styles.statNumber}>$5,000+</div>
                <div style={styles.statLabel}>Prize Value</div>
              </div>
              <div style={styles.statDivider}></div>
              <div style={styles.statItem}>
                <div style={styles.statNumber}>3</div>
                <div style={styles.statLabel}>Winners</div>
              </div>
              <div style={styles.statDivider}></div>
              <div style={styles.statItem}>
                <div style={styles.statNumber}>450+</div>
                <div style={styles.statLabel}>Participants</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div style={styles.scrollIndicator} className="bounce">
          <div style={styles.scrollText}>Scroll to explore</div>
          <div style={styles.scrollArrow}>↓</div>
        </div>
      </section>

      {/* Rewards Showcase */}
      <section style={styles.rewardsSection} id="rewards-section">
        <div style={styles.rewardsContainer}>
          <h2 style={styles.rewardsTitle}>Win Amazing Prizes</h2>
          <p style={styles.rewardsSubtitle}>
            The best ideas deserve recognition and rewards
          </p>

          <div style={styles.podiumGrid}>
            {prizes.map((prize, index) => {
              const isCenter = prize.position === 'center';
              return (
                <div
                  key={index}
                  style={{
                    ...styles.prizeCard,
                    ...(isCenter ? styles.centerCard : {}),
                    border: `3px solid ${prize.color}`,
                    order: prize.position === 'center' ? 2 : prize.position === 'left' ? 1 : 3
                  }}
                  className="prize-card"
                >
                  <div style={{ ...styles.placeBadge, background: prize.color }}>
                    {prize.place}
                  </div>

                  <div style={styles.prizeImageWrapper}>
                    <img src={prize.image} alt={prize.prize} style={styles.prizeImage} />
                  </div>

                  <div style={styles.prizeContent}>
                    <div style={styles.prizeIcon}>
                      {isCenter ? <Trophy size={32} color={prize.color} /> :
                        prize.position === 'left' ? <Award size={28} color={prize.color} /> :
                          <Medal size={28} color={prize.color} />}
                    </div>
                    <h3 style={styles.prizeTitle}>{prize.title}</h3>
                    <p style={styles.prizeName}>{prize.prize}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Participate Section */}
      <section style={styles.whyParticipateSection}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <Sparkles size={32} color="#F4CE7B" />
            <h2 style={styles.sectionTitle}>Why Participate?</h2>
          </div>
          <p style={styles.sectionSubtitle}>
            Join a community of innovators shaping the future of healthcare
          </p>

          <div style={styles.benefitsGrid}>
            {[
              {
                icon: <Trophy size={32} color="#F4CE7B" />,
                title: "Win Premium Tech Prizes",
                desc: "Top 3 winners receive cutting-edge technology worth over $5,000",
                color: "#F4CE7B"
              },
              {
                icon: <Users size={32} color="#1D57D5" />,
                title: "Network with Experts",
                desc: "Connect with leading healthcare professionals and innovators",
                color: "#1D57D5"
              },
              {
                icon: <Target size={32} color="#28C08A" />,
                title: "Make Real Impact",
                desc: "Your solution could transform healthcare for millions of patients",
                color: "#28C08A"
              },
              {
                icon: <Zap size={32} color="#F4CE7B" />,
                title: "Fast-Track Development",
                desc: "Get mentorship and resources to bring your idea to life",
                color: "#F4CE7B"
              },
              {
                icon: <Star size={32} color="#1D57D5" />,
                title: "Build Your Portfolio",
                desc: "Showcase your innovation skills to future employers and collaborators",
                color: "#1D57D5"
              },
              {
                icon: <TrendingUp size={32} color="#28C08A" />,
                title: "Career Opportunities",
                desc: "Open doors to exciting roles in healthcare technology",
                color: "#28C08A"
              }
            ].map((benefit, index) => (
              <div
                key={index}
                style={styles.benefitCard}
                className={`benefit-card fade-in-up-delay-${Math.min(index + 1, 5)}`}
              >
                <div style={{
                  ...styles.benefitIconWrapper,
                  background: `rgba(${benefit.color === '#F4CE7B' ? '244, 206, 123' : benefit.color === '#1D57D5' ? '29, 87, 213' : '40, 192, 138'}, 0.1)`,
                  color: benefit.color
                }}>
                  {benefit.icon}
                </div>
                <h3 style={styles.benefitTitle}>{benefit.title}</h3>
                <p style={styles.benefitText}>
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-Step Form */}
      <section style={styles.formSection} id="submission-form">
        <div style={styles.formContainer}>
          {!submitted ? (
            <div style={styles.formWrapper}>
              <h2 style={styles.formTitle}>Share Your Vision</h2>
              <p style={styles.formSubtitle}>
                Your idea could revolutionize healthcare – we make it simple to share
              </p>

              {/* Enhanced Step Indicators */}
              <div style={styles.stepIndicators}>
                {[
                  { num: 1, icon: '👤', label: 'Identity', completed: currentStep > 1 },
                  { num: 2, icon: '🔬', label: 'Problem', completed: currentStep > 2 },
                  { num: 3, icon: '💡', label: 'Experience', completed: currentStep > 3 },
                  { num: 4, icon: '🚀', label: 'Solution', completed: currentStep > 4 },
                  { num: 5, icon: '✅', label: 'Agreement', completed: currentStep > 5 },
                  { num: 6, icon: '📧', label: 'Contact', completed: currentStep > 6 }
                ].map((step, index) => (
                  <div key={step.num} style={styles.stepIndicatorWrapper}>
                    <div style={{
                      ...styles.stepIndicatorDot,
                      ...(currentStep === step.num ? styles.stepIndicatorActive : {}),
                      ...(step.completed ? styles.stepIndicatorCompleted : {})
                    }}>
                      {step.completed ? (
                        <CheckCircle size={20} color="#FFFFFF" />
                      ) : (
                        <span style={styles.stepIndicatorIcon}>{step.icon}</span>
                      )}
                    </div>
                    <span style={{
                      ...styles.stepIndicatorLabel,
                      ...(currentStep === step.num ? styles.stepIndicatorLabelActive : {})
                    }}>
                      {step.label}
                    </span>
                    {index < 5 && <div style={styles.stepIndicatorLine}></div>}
                  </div>
                ))}
              </div>

              {/* Progress Bar */}
              <div style={styles.progressBarContainer}>
                <div style={{ ...styles.progressBar, width: `${progress}%` }}>
                  <div style={styles.progressGlow}></div>
                </div>
              </div>
              <p style={styles.progressText}>
                Step {currentStep} of {totalSteps} • {Math.round(progress)}% Complete
              </p>

              <form onSubmit={handleSubmit} style={styles.form}>
                {/* Step 1: Identity */}
                {currentStep === 1 && (
                  <div style={styles.stepContent}>
                    <h3 style={styles.stepTitle}>I am a...</h3>
                    <div style={styles.identityGrid}>
                      {identityOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, identity: option.id })}
                          style={{
                            ...styles.identityCard,
                            ...(formData.identity === option.id ? styles.identityCardActive : {})
                          }}
                        >
                          <span style={styles.identityIcon}>{option.icon}</span>
                          <span style={styles.identityLabel}>{option.label}</span>
                          {formData.identity === option.id && (
                            <CheckCircle size={24} color="#1D57D5" style={styles.checkIcon} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Domain & Problem */}
                {currentStep === 2 && (
                  <div style={styles.stepContent}>
                    <h3 style={styles.stepTitle}>Tell us about the problem</h3>

                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Which domain are you interested in solving?</label>
                      <select
                        value={formData.domain}
                        onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                        style={styles.selectInput}
                      >
                        <option value="">Select a domain...</option>
                        {domainOptions.map((domain) => (
                          <option key={domain} value={domain}>{domain}</option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Describe a medical/healthcare problem you want to work on</label>
                      <textarea
                        value={formData.problemDescription}
                        onChange={(e) => setFormData({ ...formData, problemDescription: e.target.value })}
                        placeholder="Explain the problem you've identified in healthcare..."
                        style={styles.textArea}
                        rows={4}
                        maxLength={500}
                      />
                      <p style={styles.charCount}>{formData.problemDescription.length}/500 characters</p>
                    </div>

                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Why do you think this problem is important?</label>
                      <textarea
                        value={formData.problemImportance}
                        onChange={(e) => setFormData({ ...formData, problemImportance: e.target.value })}
                        placeholder="Share why solving this problem matters..."
                        style={styles.textArea}
                        rows={4}
                        maxLength={500}
                      />
                      <p style={styles.charCount}>{formData.problemImportance.length}/500 characters</p>
                    </div>
                  </div>
                )}

                {/* Step 3: Experience */}
                {currentStep === 3 && (
                  <div style={styles.stepContent}>
                    <h3 style={styles.stepTitle}>Your Experience</h3>

                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Have you ever developed a project/prototype before?</label>
                      <div style={styles.radioGroup}>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, hasPrototype: 'yes', prototypeDetails: formData.prototypeDetails })}
                          style={{
                            ...styles.radioButton,
                            ...(formData.hasPrototype === 'yes' ? styles.radioButtonActive : {})
                          }}
                        >
                          {formData.hasPrototype === 'yes' && <CheckCircle size={20} color="#1D57D5" />}
                          <span>Yes</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, hasPrototype: 'no', prototypeDetails: '' })}
                          style={{
                            ...styles.radioButton,
                            ...(formData.hasPrototype === 'no' ? styles.radioButtonActive : {})
                          }}
                        >
                          {formData.hasPrototype === 'no' && <CheckCircle size={20} color="#1D57D5" />}
                          <span>No</span>
                        </button>
                      </div>
                    </div>

                    {formData.hasPrototype === 'yes' && (
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>If yes, describe briefly:</label>
                        <textarea
                          value={formData.prototypeDetails}
                          onChange={(e) => setFormData({ ...formData, prototypeDetails: e.target.value })}
                          placeholder="Tell us about your previous project or prototype..."
                          style={styles.textArea}
                          rows={4}
                          maxLength={300}
                        />
                        <p style={styles.charCount}>{formData.prototypeDetails.length}/300 characters</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 4: Solution Plan */}
                {currentStep === 4 && (
                  <div style={styles.stepContent}>
                    <h3 style={styles.stepTitle}>Your Solution Plan</h3>

                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>What solution idea do you currently have for your chosen problem?</label>
                      <textarea
                        value={formData.solutionIdea}
                        onChange={(e) => setFormData({ ...formData, solutionIdea: e.target.value })}
                        placeholder="Describe your innovative solution approach..."
                        style={styles.textArea}
                        rows={5}
                        maxLength={600}
                      />
                      <p style={styles.charCount}>{formData.solutionIdea.length}/600 characters</p>
                    </div>

                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>What type of solution do you plan to build?</label>
                      <div style={styles.solutionTypeGrid}>
                        {solutionTypes.map((type) => (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, solutionType: type.id })}
                            style={{
                              ...styles.solutionTypeCard,
                              ...(formData.solutionType === type.id ? styles.solutionTypeCardActive : {})
                            }}
                          >
                            <span style={styles.solutionTypeIcon}>{type.icon}</span>
                            <span style={styles.solutionTypeLabel}>{type.label}</span>
                            {formData.solutionType === type.id && (
                              <CheckCircle size={20} color="#1D57D5" style={styles.miniCheckIcon} />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Rate your confidence in building this project (1–5):</label>
                      <div style={styles.ratingContainer}>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            onClick={() => setFormData({ ...formData, confidenceRating: rating })}
                            style={{
                              ...styles.ratingButton,
                              ...(formData.confidenceRating >= rating ? styles.ratingButtonActive : {})
                            }}
                          >
                            {rating}
                          </button>
                        ))}
                      </div>
                      <div style={styles.ratingLabels}>
                        <span style={styles.ratingLabel}>Not Confident</span>
                        <span style={styles.ratingLabel}>Very Confident</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 5: Agreement */}
                {currentStep === 5 && (
                  <div style={styles.stepContent}>
                    <h3 style={styles.stepTitle}>Hackathon Rules & Agreement</h3>
                    <div style={styles.agreementBox}>
                      <p style={styles.agreementText}>
                        By participating in this Innovation Challenge, you agree to:
                      </p>
                      <ul style={styles.agreementList}>
                        <li>Submit only original work created by you or your team</li>
                        <li>Follow all hackathon guidelines and timelines</li>
                        <li>Respect intellectual property rights</li>
                        <li>Maintain professional conduct throughout the event</li>
                        <li>Allow GEWO to showcase your project (if selected)</li>
                      </ul>
                    </div>

                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Do you agree to follow all hackathon rules and submit original work?</label>
                      <div style={styles.radioGroup}>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, agreeRules: 'yes' })}
                          style={{
                            ...styles.radioButton,
                            ...(formData.agreeRules === 'yes' ? styles.radioButtonActive : {})
                          }}
                        >
                          {formData.agreeRules === 'yes' && <CheckCircle size={20} color="#1D57D5" />}
                          <span>Yes, I agree</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, agreeRules: 'no' })}
                          style={{
                            ...styles.radioButton,
                            ...(formData.agreeRules === 'no' ? styles.radioButtonActive : {})
                          }}
                        >
                          {formData.agreeRules === 'no' && <CheckCircle size={20} color="#1D57D5" />}
                          <span>No</span>
                        </button>
                      </div>
                      {formData.agreeRules === 'no' && (
                        <p style={styles.warningText}>
                          You must agree to the rules to participate in the challenge.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 6: Contact */}
                {currentStep === 6 && (
                  <div style={styles.stepContent}>
                    <h3 style={styles.stepTitle}>Let's stay in touch</h3>
                    <p style={styles.stepHint}>We'll need this to notify you if you win!</p>
                    <div style={styles.contactFields}>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>
                          <User size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Dr. Jane Smith"
                          style={styles.textInput}
                        />
                      </div>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>
                          <Mail size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="jane.smith@example.com"
                          style={styles.textInput}
                        />
                      </div>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>
                          <Phone size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+1 (555) 123-4567"
                          style={styles.textInput}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div style={styles.buttonGroup}>
                  {currentStep > 1 && (
                    <button type="button" onClick={handleBack} style={styles.backButton}>
                      Back
                    </button>
                  )}
                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={!canProceed()}
                      style={{
                        ...styles.nextButton,
                        ...(canProceed() ? {} : styles.buttonDisabled)
                      }}
                    >
                      Next Step
                      <ChevronRight size={20} style={{ marginLeft: '8px' }} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!canProceed() || isSubmitting}
                      style={{
                        ...styles.submitButton,
                        ...((canProceed() && !isSubmitting) ? {} : styles.buttonDisabled)
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={20} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit My Vision
                          <Trophy size={20} style={{ marginLeft: '8px' }} />
                        </>
                      )}
                    </button>
                  )}
                </div>
                {submitError && (
                  <div style={styles.errorMessage}>
                    {submitError}
                  </div>
                )}
              </form>
            </div>
          ) : (
            <div style={styles.successMessage}>
              {/* Confetti Animation */}
              <div style={styles.confettiContainer}>
                {Array.from({ length: 50 }).map((_, i) => (
                  <div
                    key={i}
                    className="confetti"
                    style={{
                      left: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 3}s`,
                      background: ['#1D57D5', '#F4CE7B', '#28C08A', '#FFD98E'][Math.floor(Math.random() * 4)]
                    }}
                  />
                ))}
              </div>

              <div style={styles.successIconWrapper}>
                <div style={styles.successIconCircle}>
                  <CheckCircle size={80} color="#FFFFFF" style={styles.successIcon} />
                </div>
              </div>

              <h2 style={styles.successTitle}>
                🎉 Your Vision Has Been Submitted!
              </h2>

              <p style={styles.successText}>
                Thank you for contributing to the future of medicine. We'll review your submission
                and notify you if you're selected as a winner.
              </p>

              <div style={styles.successStats}>
                <div style={styles.successStat}>
                  <Trophy size={32} color="#F4CE7B" />
                  <p style={styles.successStatLabel}>In the Running</p>
                  <p style={styles.successStatValue}>Top Prizes</p>
                </div>
                <div style={styles.successStat}>
                  <Users size={32} color="#1D57D5" />
                  <p style={styles.successStatLabel}>You're Among</p>
                  <p style={styles.successStatValue}>450+ Innovators</p>
                </div>
                <div style={styles.successStat}>
                  <Clock size={32} color="#28C08A" />
                  <p style={styles.successStatLabel}>We'll Respond In</p>
                  <p style={styles.successStatValue}>2-3 Weeks</p>
                </div>
              </div>

              <div style={styles.successActions}>
                <button
                  style={styles.successButtonPrimary}
                  onClick={() => window.location.href = '/'}
                >
                  <span>Return to Home</span>
                  <ChevronRight size={20} />
                </button>
                <button
                  style={styles.successButtonSecondary}
                  onClick={() => window.location.href = '/articles'}
                >
                  <span>Read Healthcare Articles</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Social Proof Counter */}
      <section style={styles.socialProofSection}>
        <div style={styles.socialProofContainer}>
          <Users size={48} color="#1D57D5" />
          <h3 style={styles.socialProofTitle}>
            Join <span style={styles.counterNumber}>450+</span> Medical Professionals driving change
          </h3>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FFFFFF',
    position: 'relative',
    overflowX: 'hidden',
    maxWidth: '100vw'
  },

  // Enhanced Animated Network Background
  networkBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100vh',
    overflow: 'hidden',
    zIndex: 0,
    pointerEvents: 'none'
  },
  networkSvg: {
    width: '100%',
    height: '100%',
    opacity: 0.4
  },
  particleOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden'
  },

  // Enhanced Hero Section
  heroSection: {
    background: 'linear-gradient(135deg, #1D57D5 0%, #1a4bb8 50%, #1D57D5 100%)',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: 'clamp(100px, 15vw, 140px) 16px clamp(60px, 10vw, 100px)',
    zIndex: 1,
    overflowX: 'hidden',
    marginTop: "25px"
  },
  heroContainer: {
    maxWidth: '1100px',
    width: '100%',
    margin: '0 auto',
    textAlign: 'center',
    padding: '0 8px',
    boxSizing: 'border-box'
  },
  heroContent: {
    position: 'relative',
    zIndex: 2,
    width: '100%'
  },
  innovationBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(244, 206, 123, 0.15)',
    border: '2px solid rgba(244, 206, 123, 0.4)',
    borderRadius: '30px',
    padding: 'clamp(8px, 2vw, 12px) clamp(16px, 3vw, 24px)',
    marginBottom: 'clamp(20px, 4vw, 32px)',
    color: '#F4CE7B',
    fontSize: 'clamp(12px, 3vw, 16px)',
    fontWeight: '700',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    letterSpacing: '0.5px'
  },
  heroTitle: {
    fontSize: 'clamp(28px, 8vw, 80px)',
    fontWeight: '900',
    color: 'white',
    lineHeight: '1.15',
    marginBottom: 'clamp(16px, 4vw, 32px)',
    letterSpacing: '-1px',
    textShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
    wordBreak: 'break-word'
  },
  titleHighlight: {
    color: '#F4CE7B',
    position: 'relative'
  },
  titleGradient: {
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFE5A0 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  heroSubtitle: {
    fontSize: 'clamp(14px, 4vw, 22px)',
    color: 'rgba(255, 255, 255, 0.95)',
    lineHeight: '1.6',
    marginBottom: 'clamp(24px, 5vw, 40px)',
    fontWeight: '400',
    maxWidth: '800px',
    margin: '0 auto clamp(24px, 5vw, 40px)',
    padding: '0 8px'
  },
  subtitleEmphasis: {
    color: '#F4CE7B',
    fontWeight: '600',
    fontSize: 'clamp(13px, 3.5vw, 20px)'
  },
  keyPoints: {
    display: 'flex',
    justifyContent: 'center',
    gap: 'clamp(12px, 4vw, 40px)',
    marginBottom: 'clamp(32px, 6vw, 48px)',
    flexWrap: 'wrap',
    padding: '0 8px'
  },
  keyPoint: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  keyPointIcon: {
    width: 'clamp(28px, 6vw, 32px)',
    height: 'clamp(28px, 6vw, 32px)',
    background: 'rgba(244, 206, 123, 0.15)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid rgba(244, 206, 123, 0.4)',
    flexShrink: 0
  },
  keyPointText: {
    fontSize: 'clamp(13px, 3.5vw, 16px)',
    color: 'white',
    fontWeight: '500'
  },
  heroButtons: {
    display: 'flex',
    gap: 'clamp(12px, 3vw, 20px)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 'clamp(32px, 8vw, 60px)',
    flexWrap: 'wrap',
    padding: '0 8px'
  },
  heroCTA: {
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
    color: '#1D57D5',
    border: 'none',
    borderRadius: '50px',
    padding: 'clamp(14px, 3vw, 20px) clamp(24px, 5vw, 40px)',
    fontSize: 'clamp(14px, 3.5vw, 18px)',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: '0 10px 40px rgba(244, 206, 123, 0.5)',
    transition: 'all 0.3s ease',
    animation: 'pulse 2s infinite',
    whiteSpace: 'nowrap'
  },
  heroSecondaryBtn: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50px',
    padding: 'clamp(12px, 2.5vw, 18px) clamp(20px, 4vw, 36px)',
    fontSize: 'clamp(13px, 3vw, 16px)',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap'
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'clamp(16px, 5vw, 40px)',
    background: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '16px',
    padding: 'clamp(20px, 4vw, 30px) clamp(16px, 4vw, 50px)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    maxWidth: '700px',
    width: '100%',
    margin: '0 auto',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    flexWrap: 'wrap',
    boxSizing: 'border-box'
  },
  statItem: {
    textAlign: 'center',
    minWidth: '80px'
  },
  statNumber: {
    fontSize: 'clamp(24px, 6vw, 36px)',
    fontWeight: '800',
    color: '#F4CE7B',
    marginBottom: '4px',
    textShadow: '0 2px 10px rgba(244, 206, 123, 0.3)'
  },
  statLabel: {
    fontSize: 'clamp(11px, 2.5vw, 14px)',
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  statDivider: {
    width: '1px',
    height: '40px',
    background: 'rgba(255, 255, 255, 0.2)'
  },
  scrollIndicator: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.7)',
    zIndex: 3
  },
  scrollText: {
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '8px',
    letterSpacing: '1px',
    textTransform: 'uppercase'
  },
  scrollArrow: {
    fontSize: '24px',
    animation: 'bounce 2s infinite'
  },

  // Rewards Section
  rewardsSection: {
    padding: '100px 24px',
    background: 'linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%)',
    position: 'relative',
    zIndex: 1
  },
  rewardsContainer: {
    maxWidth: '1200px',
    margin: '0 auto'
  },
  rewardsTitle: {
    fontSize: '48px',
    fontWeight: '800',
    color: '#1a202c',
    textAlign: 'center',
    marginBottom: '16px'
  },
  rewardsSubtitle: {
    fontSize: '20px',
    color: '#718096',
    textAlign: 'center',
    marginBottom: '60px'
  },
  podiumGrid: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: '32px',
    flexWrap: 'wrap'
  },
  prizeCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: '24px',
    padding: '32px',
    width: '100%',
    maxWidth: '320px',
    position: 'relative',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    WebkitBackdropFilter: 'blur(10px)'
  },
  centerCard: {
    transform: 'scale(1.1)',
    zIndex: 2,
    boxShadow: '0 16px 48px rgba(244, 206, 123, 0.3)'
  },
  placeBadge: {
    position: 'absolute',
    top: '-16px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '8px 24px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '700',
    color: 'white',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
  },
  prizeImageWrapper: {
    width: '100%',
    height: '200px',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: '24px',
    background: '#f7f9fc'
  },
  prizeImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  prizeContent: {
    textAlign: 'center'
  },
  prizeIcon: {
    marginBottom: '16px'
  },
  prizeTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '8px'
  },
  prizeName: {
    fontSize: '16px',
    color: '#718096',
    fontWeight: '500'
  },

  // Enhanced Form Section
  formSection: {
    padding: 'clamp(80px, 10vw, 120px) 24px',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 50%, #F1F5F9 100%)',
    position: 'relative',
    zIndex: 1
  },
  formContainer: {
    maxWidth: '800px',
    margin: '0 auto'
  },
  formWrapper: {
    background: 'linear-gradient(145deg, #FFFFFF 0%, #FAFBFC 100%)',
    borderRadius: '32px',
    padding: 'clamp(32px, 6vw, 56px)',
    boxShadow: '0 25px 80px -12px rgba(0, 0, 0, 0.12), 0 10px 32px -8px rgba(29, 87, 213, 0.08)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    position: 'relative',
    overflow: 'hidden'
  },
  formTitle: {
    fontSize: 'clamp(32px, 6vw, 48px)',
    fontWeight: '900',
    color: '#1a202c',
    textAlign: 'center',
    marginBottom: '16px',
    background: 'linear-gradient(135deg, #1D57D5 0%, #2563eb 50%, #7C3AED 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: '-1px'
  },
  formSubtitle: {
    fontSize: 'clamp(16px, 3vw, 20px)',
    color: '#64748B',
    textAlign: 'center',
    marginBottom: '48px',
    lineHeight: '1.6'
  },
  stepIndicators: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginBottom: '40px',
    position: 'relative',
    padding: '0 8px',
    gap: '8px'
  },
  stepIndicatorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    flex: '0 1 auto',
    minWidth: '48px',
    maxWidth: '80px'
  },
  stepIndicatorDot: {
    width: '44px',
    height: '44px',
    borderRadius: '14px',
    background: 'linear-gradient(145deg, #F8FAFC 0%, #E2E8F0 100%)',
    border: '2px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    marginBottom: '8px',
    zIndex: 2,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
  },
  stepIndicatorActive: {
    background: 'linear-gradient(135deg, #1D57D5 0%, #2563eb 100%)',
    border: '2px solid #1D57D5',
    boxShadow: '0 6px 20px rgba(29, 87, 213, 0.35)',
    transform: 'scale(1.1)'
  },
  stepIndicatorCompleted: {
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    border: '2px solid #10B981',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
  },
  stepIndicatorIcon: {
    fontSize: '18px'
  },
  stepIndicatorLabel: {
    fontSize: '9px',
    fontWeight: '700',
    color: '#94A3B8',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    maxWidth: '60px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  stepIndicatorLabelActive: {
    color: '#1D57D5',
    fontSize: '9px'
  },
  stepIndicatorLine: {
    display: 'none'
  },
  progressBarContainer: {
    width: '100%',
    height: '10px',
    background: '#E2E8F0',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '12px',
    position: 'relative'
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #1D57D5 0%, #2563eb 50%, #1D57D5 100%)',
    transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    borderRadius: '8px',
    position: 'relative',
    backgroundSize: '200% 100%',
    animation: 'shimmer 2s infinite'
  },
  progressGlow: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '40px',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
    animation: 'progressGlow 2s infinite'
  },
  progressText: {
    fontSize: '14px',
    color: '#1D57D5',
    textAlign: 'center',
    marginBottom: '32px',
    fontWeight: '700'
  },
  form: {
    width: '100%'
  },
  stepContent: {
    minHeight: '300px',
    marginBottom: '32px'
  },
  stepTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '12px'
  },
  stepHint: {
    fontSize: '16px',
    color: '#718096',
    marginBottom: '24px'
  },

  // Identity Cards
  identityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px'
  },
  identityCard: {
    background: 'white',
    border: '2px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    transition: 'all 0.2s ease',
    position: 'relative'
  },
  identityCardActive: {
    border: '2px solid #1D57D5',
    background: 'rgba(29, 87, 213, 0.05)',
    boxShadow: '0 4px 16px rgba(29, 87, 213, 0.2)'
  },
  identityIcon: {
    fontSize: '40px'
  },
  identityLabel: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a202c',
    textAlign: 'center'
  },
  checkIcon: {
    position: 'absolute',
    top: '12px',
    right: '12px'
  },

  // Input Fields
  textInput: {
    width: '100%',
    padding: '16px 20px',
    fontSize: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit'
  },
  textArea: {
    width: '100%',
    padding: '16px 20px',
    fontSize: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  charCount: {
    fontSize: '14px',
    color: '#a0aec0',
    marginTop: '8px',
    textAlign: 'right'
  },

  // Contact Fields
  contactFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568'
  },

  // Buttons
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginTop: '32px'
  },
  backButton: {
    background: 'white',
    color: '#4a5568',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  nextButton: {
    background: '#1D57D5',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    transition: 'all 0.2s ease'
  },
  submitButton: {
    background: 'linear-gradient(135deg, #1D57D5 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 40px',
    fontSize: '18px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    boxShadow: '0 8px 24px rgba(29, 87, 213, 0.3)',
    transition: 'all 0.2s ease'
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  errorMessage: {
    background: 'rgba(220, 53, 69, 0.1)',
    border: '1px solid rgba(220, 53, 69, 0.3)',
    borderRadius: '12px',
    padding: '16px 20px',
    marginTop: '16px',
    color: '#dc3545',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center'
  },

  // Enhanced Success Message
  successMessage: {
    textAlign: 'center',
    padding: '60px 20px',
    position: 'relative',
    minHeight: '600px'
  },
  confettiContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 0
  },
  successIconWrapper: {
    position: 'relative',
    zIndex: 1,
    marginBottom: '32px'
  },
  successIconCircle: {
    width: '160px',
    height: '160px',
    margin: '0 auto',
    background: 'linear-gradient(135deg, #1D57D5 0%, #2563eb 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 20px 60px rgba(29, 87, 213, 0.4)',
    animation: 'successPulse 2s infinite'
  },
  successIcon: {
    animation: 'successPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },
  successTitle: {
    fontSize: '42px',
    fontWeight: '800',
    color: '#1a202c',
    marginBottom: '20px',
    position: 'relative',
    zIndex: 1
  },
  successText: {
    fontSize: '18px',
    color: '#718096',
    lineHeight: '1.6',
    marginBottom: '40px',
    maxWidth: '600px',
    margin: '0 auto 40px',
    position: 'relative',
    zIndex: 1
  },
  successStats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginBottom: '40px',
    flexWrap: 'wrap',
    position: 'relative',
    zIndex: 1
  },
  successStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 32px',
    background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)',
    borderRadius: '16px',
    border: '2px solid #E2E8F0',
    minWidth: '160px'
  },
  successStatLabel: {
    fontSize: '13px',
    color: '#718096',
    marginTop: '12px',
    marginBottom: '4px',
    fontWeight: '500'
  },
  successStatValue: {
    fontSize: '18px',
    color: '#1a202c',
    fontWeight: '700'
  },
  successActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    position: 'relative',
    zIndex: 1
  },
  successButtonPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #1D57D5 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(29, 87, 213, 0.3)'
  },
  successButtonSecondary: {
    background: 'white',
    color: '#1D57D5',
    border: '2px solid #1D57D5',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },

  // Social Proof
  socialProofSection: {
    padding: '60px 24px',
    background: 'linear-gradient(135deg, #EBF4FF 0%, #F7F9FC 100%)',
    position: 'relative',
    zIndex: 1
  },
  socialProofContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px'
  },
  socialProofTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#1a202c'
  },
  counterNumber: {
    color: '#1D57D5',
    fontWeight: '800',
    fontSize: '32px'
  },

  // Enhanced Why Participate Section
  whyParticipateSection: {
    padding: 'clamp(80px, 10vw, 120px) 24px',
    background: '#FFFFFF',
    position: 'relative',
    overflow: 'hidden'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '16px'
  },
  sectionTitle: {
    fontSize: '48px',
    fontWeight: '800',
    color: '#1a202c',
    textAlign: 'center'
  },
  sectionSubtitle: {
    fontSize: '20px',
    color: '#718096',
    textAlign: 'center',
    marginBottom: '60px',
    maxWidth: '700px',
    margin: '0 auto 60px'
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 'clamp(24px, 4vw, 40px)',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  benefitCard: {
    background: '#FFFFFF',
    border: '1px solid #F1F5F9',
    borderRadius: '24px',
    padding: '40px 32px',
    textAlign: 'left',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    position: 'relative',
    zIndex: 1,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
    overflow: 'hidden'
  },
  benefitIconWrapper: {
    width: '72px',
    height: '72px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
    transition: 'all 0.4s ease'
  },
  benefitTitle: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#1a202c',
    marginBottom: '16px',
    letterSpacing: '-0.5px',
    lineHeight: '1.2'
  },
  benefitText: {
    fontSize: '16px',
    color: '#64748B',
    lineHeight: '1.7',
    fontWeight: '400'
  },

  // New Styles for Enhanced Form
  selectInput: {
    width: '100%',
    padding: '16px 20px',
    fontSize: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    background: 'white',
    cursor: 'pointer'
  },
  radioGroup: {
    display: 'flex',
    gap: '12px'
  },
  radioButton: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '16px 24px',
    background: 'white',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '16px',
    fontWeight: '600',
    color: '#4a5568'
  },
  radioButtonActive: {
    border: '2px solid #1D57D5',
    background: 'rgba(29, 87, 213, 0.05)',
    color: '#1D57D5'
  },
  solutionTypeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '16px'
  },
  solutionTypeCard: {
    background: 'white',
    border: '2px solid #e2e8f0',
    borderRadius: '16px',
    padding: '20px 16px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    transition: 'all 0.3s ease',
    position: 'relative',
    minHeight: '110px'
  },
  solutionTypeCardActive: {
    border: '2px solid #1D57D5',
    background: 'rgba(29, 87, 213, 0.05)',
    boxShadow: '0 4px 16px rgba(29, 87, 213, 0.2)',
    transform: 'scale(1.02)'
  },
  solutionTypeIcon: {
    fontSize: '32px'
  },
  solutionTypeLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a202c',
    textAlign: 'center',
    lineHeight: '1.4'
  },
  miniCheckIcon: {
    position: 'absolute',
    top: '8px',
    right: '8px'
  },
  ratingContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginBottom: '12px'
  },
  ratingButton: {
    width: '56px',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '20px',
    fontWeight: '700',
    color: '#4a5568'
  },
  ratingButtonActive: {
    border: '2px solid #1D57D5',
    background: 'linear-gradient(135deg, #1D57D5 0%, #2563eb 100%)',
    color: 'white',
    boxShadow: '0 4px 12px rgba(29, 87, 213, 0.3)'
  },
  ratingLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: '8px',
    paddingRight: '8px'
  },
  ratingLabel: {
    fontSize: '13px',
    color: '#718096',
    fontWeight: '500'
  },
  agreementBox: {
    background: 'rgba(29, 87, 213, 0.05)',
    border: '2px solid rgba(29, 87, 213, 0.2)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  },
  agreementText: {
    fontSize: '16px',
    color: '#1a202c',
    fontWeight: '600',
    marginBottom: '16px'
  },
  agreementList: {
    fontSize: '15px',
    color: '#4a5568',
    lineHeight: '1.8',
    paddingLeft: '24px',
    margin: 0
  },
  warningText: {
    fontSize: '14px',
    color: '#E53E3E',
    marginTop: '12px',
    padding: '12px',
    background: 'rgba(229, 62, 62, 0.1)',
    borderRadius: '8px',
    border: '1px solid rgba(229, 62, 62, 0.2)'
  }
};

// Add enhanced animations and hover effects
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0% {
      box-shadow: 0 10px 40px rgba(244, 206, 123, 0.5);
      transform: scale(1);
    }
    50% {
      box-shadow: 0 10px 50px rgba(244, 206, 123, 0.7);
      transform: scale(1.02);
    }
    100% {
      box-shadow: 0 10px 40px rgba(244, 206, 123, 0.5);
      transform: scale(1);
    }
  }
  
  .pulse-button:hover {
    transform: scale(1.05) !important;
    box-shadow: 0 15px 60px rgba(244, 206, 123, 0.6) !important;
  }
  
  .network-node {
    animation: float 3s ease-in-out infinite;
  }
  
  .node-1 { animation-delay: 0s; }
  .node-2 { animation-delay: 0.2s; }
  .node-3 { animation-delay: 0.4s; }
  .node-4 { animation-delay: 0.6s; }
  .node-5 { animation-delay: 0.8s; }
  .node-6 { animation-delay: 1s; }
  .node-7 { animation-delay: 1.2s; }
  .node-8 { animation-delay: 1.4s; }
  .node-9 { animation-delay: 1.6s; }
  .node-10 { animation-delay: 1.8s; }
  .node-11 { animation-delay: 2s; }
  .node-12 { animation-delay: 2.2s; }
  .node-13 { animation-delay: 2.4s; }
  
  .network-line {
    animation: fadeInOut 4s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
      opacity: 1;
    }
    50% {
      transform: translateY(-15px);
      opacity: 0.8;
    }
  }
  
  @keyframes fadeInOut {
    0%, 100% {
      opacity: 0.2;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  /* Enhanced Benefit Card Hover Effects */
  div[style*="benefitCard"]:hover {
    transform: translateY(-12px) !important;
    box-shadow: 0 20px 60px rgba(29, 87, 213, 0.2) !important;
    border-color: #1D57D5 !important;
  }
  
  div[style*="benefitCard"]:hover div[style*="benefitIconWrapper"] {
    transform: scale(1.1) rotate(-5deg) !important;
    background: linear-gradient(135deg, #1D57D5 0%, #2563eb 100%) !important;
  }
  
  
  /* Smooth Fade In Up Animation */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .fade-in-up {
    animation: fadeInUp 0.8s ease-out;
  }
  
  .fade-in-up-delay {
    animation: fadeInUp 0.8s ease-out 0.2s backwards;
  }
  
  .fade-in-up-delay-2 {
    animation: fadeInUp 0.8s ease-out 0.4s backwards;
  }
  
  .fade-in-up-delay-3 {
    animation: fadeInUp 0.8s ease-out 0.6s backwards;
  }
  
  /* Confetti Animation */
  .confetti {
    position: absolute;
    width: 10px;
    height: 10px;
    top: -10px;
    animation: confettiFall 3s linear infinite;
  }
  
  @keyframes confettiFall {
    0% {
      transform: translateY(0) rotate(0deg);
      opacity: 1;
    }
    100% {
      transform: translateY(600px) rotate(720deg);
      opacity: 0;
    }
  }
  
  /* Success Animations */
  @keyframes successPulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 20px 60px rgba(29, 87, 213, 0.4);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 25px 70px rgba(29, 87, 213, 0.5);
    }
  }
  
  @keyframes successPop {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  /* Progress Bar Animations */
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
  
  @keyframes progressGlow {
    0%, 100% {
      transform: translateX(-40px);
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
  }
  
  /* Button Hover Effects */
  button[style*="successButtonPrimary"]:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 12px 32px rgba(29, 87, 213, 0.4) !important;
  }
  
  button[style*="successButtonSecondary"]:hover {
    background: #E3EDFF !important;
    transform: translateY(-2px);
  }
  
  /* Input Focus Effects */
  input:focus, textarea:focus, select:focus {
    border-color: #1D57D5 !important;
    box-shadow: 0 0 0 4px rgba(29, 87, 213, 0.1) !important;
    outline: none !important;
  }
  
  /* Step Transition Animation */
  @keyframes stepTransition {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  div[style*="stepContent"] {
    animation: stepTransition 0.4s ease-out;
  }

  /* Mobile Responsive Adjustments */
  @media (max-width: 768px) {
    div[style*="benefitsGrid"] {
      grid-template-columns: 1fr !important;
      gap: 20px !important;
    }
    
    
    div[style*="stepIndicators"] {
      display: none !important;
    }
    
    div[style*="successStats"] {
      flex-direction: column !important;
      gap: 16px !important;
    }
  }
  
  .particle {
    position: absolute;
    width: 3px;
    height: 3px;
    background: #F4CE7B;
    border-radius: 50%;
    animation: rise linear infinite;
    opacity: 0;
    box-shadow: 0 0 10px #F4CE7B;
  }
  
  @keyframes rise {
    0% {
      bottom: -10px;
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    90% {
      opacity: 1;
    }
    100% {
      bottom: 100vh;
      opacity: 0;
    }
  }
  
  @keyframes bounce {
    0%, 100% {
      transform: translateY(0) translateX(-50%);
    }
    50% {
      transform: translateY(-10px) translateX(-50%);
    }
  }
  
  .fade-in-up {
    animation: fadeInUp 0.8s ease-out forwards;
  }
  
  .fade-in-up-delay {
    animation: fadeInUp 0.8s ease-out 0.2s forwards;
    opacity: 0;
  }
  
  .fade-in-up-delay-2 {
    animation: fadeInUp 0.8s ease-out 0.4s forwards;
    opacity: 0;
  }
  
  .fade-in-up-delay-3 {
    animation: fadeInUp 0.8s ease-out 0.6s forwards;
    opacity: 0;
  }
  
  .fade-in-up-delay-4 {
    animation: fadeInUp 0.8s ease-out 0.8s forwards;
    opacity: 0;
  }
  
  .fade-in-up-delay-5 {
    animation: fadeInUp 0.8s ease-out 1s forwards;
    opacity: 0;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .prize-card:hover {
    transform: translateY(-8px) !important;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15) !important;
  }

  .benefit-card:hover {
    transform: translateY(-12px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08) !important;
    border-color: rgba(29, 87, 213, 0.2) !important;
  }

  .benefit-card:hover div:first-child {
    transform: scale(1.1) rotate(-5deg);
  }
  
  input:focus, textarea:focus {
    border-color: #1D57D5 !important;
    box-shadow: 0 0 0 3px rgba(29, 87, 213, 0.1) !important;
  }
  
  @media (max-width: 768px) {
    .hero-title {
      font-size: 42px !important;
      letter-spacing: -1px !important;
    }
    
    .stats-bar {
      flex-direction: column !important;
      gap: 20px !important;
      padding: 20px 30px !important;
    }
    
    .stat-divider {
      display: none !important;
    }
    
    .key-points {
      flex-direction: column !important;
      gap: 16px !important;
    }
    
    .hero-buttons {
      flex-direction: column !important;
      width: 100% !important;
    }
    
    .hero-buttons button {
      width: 100% !important;
      justify-content: center !important;
    }
    
    .podium-grid {
      flex-direction: column !important;
    }
    
    .prize-card {
      width: 100% !important;
      max-width: 400px !important;
    }
    
    .center-card {
      transform: scale(1) !important;
      order: 1 !important;
    }
    
    .identity-grid {
      grid-template-columns: 1fr !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default InnovationChallenge;
